// ============================================================================
// Componente: FiscalAutomacaoApuracao.js (Apuração ICMS e PIS/COFINS)
// Objetivo: Interface da apuração de impostos. Vive no módulo Fiscal.
// Conteúdo embutido: quando `embedded` é true, é renderizada DENTRO do casco
//           Fiscal.js (que já traz Sidebar + cabeçalho), então não desenha
//           casco próprio. Default false = página inteira (compatibilidade).
// ============================================================================

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../Sidebar';
import {
    Landmark, Download, AlertCircle, CheckCircle2,
    ArrowLeft, Timer, FileSpreadsheet, Eraser,
    HelpCircle, Database, UploadCloud, Check ,Loader2
} from 'lucide-react';
import HelpCenter from '../../HelpCenter';

const AutomacaoApuracao = ({ embedded = false }) => {
    const navigate = useNavigate();
    
    // Controle de Layout
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [isCollapsed, setIsCollapsed] = useState(window.innerWidth <= 768);
    const [isHelpOpen, setIsHelpOpen] = useState(false);
    
    // Regras de Perfil do Usuário
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const nomeUsuario = user?.nome ? user.nome.split(' ')[0] : 'Colaborador';

    const [mesAno, setMesAno] = useState('');
    const [arquivosBase, setArquivosBase] = useState([]);
    const [carregando, setCarregando] = useState(false);
    const [mensagem, setMensagem] = useState(null);
    const [arquivosGerados, setArquivosGerados] = useState([]);
    const [executionId, setExecutionId] = useState(null);
    
    const [progresso, setProgresso] = useState(0);
    const [statusText, setStatusText] = useState('Aguardando arquivos...');

    useEffect(() => {
        const handleResize = () => { 
            const mobile = window.innerWidth <= 768;
            setIsMobile(mobile);
            if (mobile) setIsCollapsed(true);
        };
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const getMarginLeft = () => isMobile ? '0px' : (isCollapsed ? '80px' : '260px');

    const iniciarSimulacaoProgresso = () => {
        setProgresso(0);
        setStatusText('Iniciando processamento...');
        const interval = setInterval(() => {
            setProgresso((oldProgress) => {
                if (oldProgress >= 95) {
                    clearInterval(interval);
                    setStatusText('Finalizando apuração...');
                    return 95;
                }
                const diff = Math.random() * 8;
                const newProgress = Math.min(oldProgress + diff, 95);
                
                // Textos dinâmicos
                if (newProgress < 30) setStatusText('Lendo arquivos de entrada...');
                else if (newProgress < 60) setStatusText('Cruzando dados fiscais...');
                else if (newProgress < 85) setStatusText('Calculando impostos...');
                else setStatusText('Gerando relatórios...');
                
                return newProgress;
            });
        }, 800);
        return interval;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setCarregando(true);
        setMensagem(null);
        setArquivosGerados([]);
        
        const newId = Date.now().toString();
        setExecutionId(newId);

        const progressoInterval = iniciarSimulacaoProgresso();
        const formData = new FormData();
        formData.append('executionId', newId);
        formData.append('mesAno', mesAno);
        for (let i = 0; i < arquivosBase.length; i++) {
            formData.append('arquivos_base', arquivosBase[i]);
        }

        try {
            const response = await fetch('/api/fiscal/apuracao-impostos', {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
                body: formData
            });

            const data = await response.json();

            if (response.ok && data.success) {
                clearInterval(progressoInterval);
                setProgresso(100);
                setStatusText('Apuração Concluída!');
                
                setTimeout(() => {
                    setMensagem({ type: 'success', text: 'Apuração concluída com sucesso! Os downloads iniciarão automaticamente.' });
                    setArquivosGerados(data.files);
                    setCarregando(false);
                    
                    data.files.forEach((fileName, index) => {
                        setTimeout(() => {
                            handleDownload(fileName, newId);
                        }, index * 800); 
                    });

                }, 600);
            } else {
                clearInterval(progressoInterval);
                setCarregando(false);
                setMensagem({ type: 'error', text: data.error || 'Erro ao processar.' });
            }
        } catch (error) {
            clearInterval(progressoInterval);
            setCarregando(false);
            setMensagem({ type: 'error', text: 'Erro de conexão com o servidor.' });
        }
    };

    const handleDownload = async (fileName, idOpcional) => {
        const idParaUsar = idOpcional || executionId; 

        try {
            const response = await fetch(`/api/fiscal/download-apuracao/${idParaUsar}/${fileName}`, {
                method: 'GET',
                headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
            });

            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', fileName);
                document.body.appendChild(link);
                link.click();
                link.parentNode.removeChild(link);
                window.URL.revokeObjectURL(url); 
            } else {
                alert("O arquivo não está mais disponível.");
            }
        } catch (error) {
            alert("Erro ao transferir o arquivo.");
        }
    };

    const handleLimpar = () => {
        setMesAno('');
        setArquivosBase([]);
        setMensagem(null);
        setArquivosGerados([]);
        setProgresso(0);
        setStatusText('Aguardando arquivos...');
        setExecutionId(null);

        const inputFile = document.getElementById('input-base');
        if (inputFile) {
            inputFile.value = '';
        }
    };

    const corModulo = '#7c3aed'; // Roxo Fiscal
    
    const getProgressBarColor = () => {
        if (mensagem?.type === 'error') return '#ef4444'; // Vermelho
        if (mensagem?.type === 'success') return '#10b981'; // Verde
        return corModulo; // Roxo Padrão (Processando)
    };

    return (
        <div style={embedded ? { width: '100%' } : { display: 'flex', minHeight: '100vh', backgroundColor: '#f4f6f8' }}>
            {!embedded && <Sidebar isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} />}

            <div style={{ flex: 1, marginLeft: embedded ? 0 : getMarginLeft(), transition: 'margin-left 0.3s ease', width: '100%' }}>

                {/* CABEÇALHO PREMIUM — só na versão página inteira; embutido herda o do Fiscal.js */}
                {!embedded && (
                <div style={{
                    height: '85px', backgroundColor: '#fff', borderBottom: '1px solid #e2e8f0',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)', display: 'flex',
                    alignItems: 'center', justifyContent: 'space-between',
                    padding: isMobile ? '0 20px 0 60px' : '0 40px', position: 'sticky', top: 0, zIndex: 20
                }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <span style={{ fontSize: '12px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <button onClick={() => navigate('/fiscal')} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center' }}>
                                <ArrowLeft size={14} /> Voltar
                            </button>
                            <span style={{ margin: '0 4px' }}>/</span> Fiscal <span style={{ margin: '0 4px' }}>/</span> Apuração
                        </span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                            <h2 style={{ margin: 0, fontSize: '22px', fontWeight: '800', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <Landmark size={24} color={corModulo} /> Apuração ICMS e PIS/COFINS
                            </h2>
                            <button
                                type="button"
                                onClick={() => setIsHelpOpen(true)}
                                style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '4px 12px', backgroundColor: '#f5f0ff', color: corModulo, border: '1px solid #ddd6fe', borderRadius: '99px', fontWeight: '600', cursor: 'pointer', fontSize: '12px', transition: 'all 0.2s' }}
                            >
                                <HelpCircle size={14} /> Como Utilizar
                            </button>
                        </div>
                    </div>

                    {!isMobile && (
                        <span style={{ fontSize: '15px', fontWeight: '600', color: '#475569' }}>
                            Olá, {nomeUsuario} 👋
                        </span>
                    )}
                </div>
                )}

                <div style={{ padding: embedded ? 0 : (isMobile ? '20px' : '40px') }} className="max-w-4xl mx-auto">
                    
                    <div style={{ backgroundColor: '#fff', padding: isMobile ? '20px' : '30px', borderRadius: '16px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', marginBottom: '30px' }}>
                        
                        <div style={{ backgroundColor: '#f5f0ff', borderLeft: `4px solid ${corModulo}`, padding: '16px 20px', borderRadius: '8px', marginBottom: '24px' }}>
                            <h4 style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#5b21b6', fontSize: '15px', fontWeight: '700', marginBottom: '8px', margin: 0 }}>
                                <Database size={18} /> Apuração de Impostos
                            </h4>
                            <p style={{ color: '#4c1d95', fontSize: '14px', lineHeight: '1.6', margin: 0, marginTop: '4px' }}>
                                Faça o upload dos arquivos base (.xlsx) contendo as informações financeiras. O sistema irá realizar a conferência e o cálculo automático dos impostos.
                            </p>
                        </div>

                        <form onSubmit={handleSubmit}>
                            <div style={{ marginBottom: '24px' }}>
                                <div style={{ 
                                    border: '2px dashed #cbd5e1', borderRadius: '12px', padding: '40px 20px', 
                                    textAlign: 'center', backgroundColor: '#f8fafc', cursor: 'pointer',
                                    transition: 'all 0.2s ease', position: 'relative'
                                }}>
                                    <input 
                                        id="input-base"
                                        type="file" 
                                        multiple 
                                        accept=".xlsx"
                                        onChange={(e) => { setArquivosBase(e.target.files); setMensagem(null); }} 
                                        style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer' }}
                                        required
                                        disabled={carregando}
                                    />
                                    
                                    {arquivosBase.length === 0 ? (
                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                                            <div style={{ backgroundColor: '#f1f5f9', padding: '16px', borderRadius: '50%', color: '#94a3b8' }}>
                                                <UploadCloud size={32} />
                                            </div>
                                            <div>
                                                <p style={{ margin: '0 0 4px 0', fontWeight: '600', color: '#334155' }}>Arraste os arquivos .xlsx aqui ou clique para buscar</p>
                                                <p style={{ margin: 0, fontSize: '12px', color: '#94a3b8' }}>Suporta múltiplos arquivos Excel</p>
                                            </div>
                                        </div>
                                    ) : (
                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                                            <div style={{ backgroundColor: '#f5f0ff', padding: '16px', borderRadius: '50%', color: corModulo }}>
                                                <FileSpreadsheet size={32} />
                                            </div>
                                            <div>
                                                <p style={{ margin: '0 0 4px 0', fontWeight: '700', color: '#0f172a' }}>Arquivos Selecionados</p>
                                                <p style={{ margin: 0, fontSize: '13px', fontWeight: '600', color: corModulo }}>{arquivosBase.length} arquivo(s) carregado(s)</p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '15px' }}>
                                <button 
                                    type="button" 
                                    onClick={handleLimpar}
                                    disabled={carregando}
                                    style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 20px', borderRadius: '8px', backgroundColor: '#f1f5f9', color: '#475569', fontWeight: '600', border: '1px solid #cbd5e1', cursor: carregando ? 'not-allowed' : 'pointer' }}
                                >
                                    <Eraser size={18} /> Limpar
                                </button>
                        
                                {!arquivosGerados.length > 0 && (
                                    <button 
                                        type="submit" 
                                        disabled={carregando || arquivosBase.length === 0}
                                        style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 30px', borderRadius: '8px', backgroundColor: carregando || arquivosBase.length === 0 ? '#cbd5e1' : corModulo, color: '#fff', fontWeight: '600', border: 'none', cursor: carregando || arquivosBase.length === 0 ? 'not-allowed' : 'pointer', boxShadow: '0 4px 6px -1px rgba(124, 58, 237, 0.2)' }}
                                    >
                                        {carregando ? <><Timer size={18} className="animate-spin" /> Processando...</> : <><Landmark size={18} /> Iniciar Apuração</>}
                                    </button>
                                )}
                            </div>
                        </form>

                        {/* ALERTAS */}
                        {mensagem && !carregando && (
                            <div style={{ backgroundColor: mensagem.type === 'success' ? '#ecfdf5' : '#fef2f2', border: `1px solid ${mensagem.type === 'success' ? '#a7f3d0' : '#fecaca'}`, color: mensagem.type === 'success' ? '#059669' : '#ef4444', padding: '16px', borderRadius: '12px', display: 'flex', alignItems: 'center', gap: '12px', marginTop: '24px', fontSize: '14px', fontWeight: '500' }}>
                                {mensagem.type === 'success' ? <CheckCircle2 size={20} /> : <AlertCircle size={20} />} 
                                {mensagem.text}
                            </div>
                        )}

                        {/* LISTA DE ARQUIVOS GERADOS */}
                        {arquivosGerados.length > 0 && (
                            <div style={{ marginTop: '30px', paddingTop: '30px', borderTop: '1px solid #e2e8f0' }}>
                                <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#1e293b', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <Check size={20} color={corModulo} /> Arquivos Prontos
                                </h3>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                    {arquivosGerados.map((fileName, index) => (
                                        <div key={index} style={{ padding: '16px 20px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', transition: 'all 0.2s', ':hover': { borderColor: '#cbd5e1' } }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                                <div style={{ backgroundColor: '#ecfdf5', padding: '10px', borderRadius: '8px', color: '#10b981' }}>
                                                    <FileSpreadsheet size={24} />
                                                </div>
                                                <span style={{ fontSize: '14px', fontWeight: '600', color: '#334155' }}>{fileName}</span>
                                            </div>
                                            <button 
                                                onClick={() => handleDownload(fileName)} 
                                                style={{ display: 'flex', alignItems: 'center', gap: '8px', backgroundColor: '#10b981', color: 'white', padding: '10px 20px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontWeight: '600', transition: 'background-color 0.2s' }}
                                            >
                                                <Download size={16} /> Baixar
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* CARD DE PROGRESSO */}
                    {(carregando || progresso > 0) && arquivosGerados.length === 0 && (
                        <div style={{ backgroundColor: '#fff', borderRadius: '16px', padding: '24px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)', border: '1px solid #e2e8f0' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    {carregando && <Loader2 size={24} color={corModulo} className="animate-spin" />}
                                    {!carregando && Math.round(progresso) === 100 && <CheckCircle2 size={24} color="#10b981" />}
                                    {!carregando && mensagem?.type === 'error' && <AlertCircle size={24} color="#ef4444" />}
                                    
                                    <div>
                                        <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: '#1e293b' }}>Status da Execução</h4>
                                        <p style={{ margin: 0, fontSize: '14px', color: '#64748b', marginTop: '2px' }}>{statusText}</p>
                                    </div>
                                </div>
                                <span style={{ fontSize: '20px', fontWeight: '800', color: getProgressBarColor() }}>
                                    {Math.round(progresso)}%
                                </span>
                            </div>

                            <div style={{ width: '100%', height: '12px', backgroundColor: '#f1f5f9', borderRadius: '99px', overflow: 'hidden' }}>
                                <div style={{ 
                                    width: `${progresso}%`, 
                                    height: '100%', 
                                    backgroundColor: getProgressBarColor(),
                                    borderRadius: '99px',
                                    transition: 'width 0.5s ease-in-out, background-color 0.3s ease'
                                }} />
                            </div>
                        </div>
                    )}

                </div>
            </div>

            {/* CHAMADA DO MANUAL COM FILTRO EXCLUSIVO */}
            <HelpCenter 
                isOpen={isHelpOpen} 
                onClose={() => setIsHelpOpen(false)} 
                secaoInicial="apuracao-impostos" 
                showOnlyIds={['apuracao-impostos']} 
            />
        </div>
    );
};

export default AutomacaoApuracao;